import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../hooks/useI18n';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import {
  TrendingUp,
  Users,
  BarChart3,
  FileText,
  Activity,
  Calendar,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Download,
  Eye,
  Shield,
  Settings,
  Database,
  Server
} from 'lucide-react';

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { t, isRTL } = useI18n();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const stats = [
    {
      title: t('adminDashboard.totalUsers'),
      value: '2,847',
      change: '+18.2%',
      changeType: 'increase',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: t('adminDashboard.activeSessions'),
      value: '1,234',
      change: '+12.5%',
      changeType: 'increase',
      icon: Activity,
      color: 'bg-green-500'
    },
    {
      title: t('adminDashboard.systemLoad'),
      value: '67%',
      change: '-5.2%',
      changeType: 'decrease',
      icon: Server,
      color: 'bg-orange-500'
    },
    {
      title: t('adminDashboard.storageUsed'),
      value: '1.2TB',
      change: '+8.7%',
      changeType: 'increase',
      icon: Database,
      color: 'bg-purple-500'
    }
  ];

  const systemActivity = [
    {
      id: 1,
      action: t('adminDashboard.newUserRegistration'),
      user: 'john.doe@example.com',
      time: '1 minute ago',
      type: 'user',
      severity: 'info'
    },
    {
      id: 2,
      action: t('adminDashboard.systemBackupCompleted'),
      user: 'System',
      time: '5 minutes ago',
      type: 'system',
      severity: 'success'
    },
    {
      id: 3,
      action: t('adminDashboard.failedLoginAttempt'),
      user: 'unknown@example.com',
      time: '8 minutes ago',
      type: 'security',
      severity: 'warning'
    },
    {
      id: 4,
      action: t('adminDashboard.databaseOptimization'),
      user: 'System',
      time: '15 minutes ago',
      type: 'system',
      severity: 'info'
    },
    {
      id: 5,
      action: 'Report generation',
      user: 'admin@facturelik.com',
      time: '20 minutes ago',
      type: 'report',
      severity: 'info'
    }
  ];

  const adminActions = [
    {
      title: t('adminDashboard.userManagement'),
      description: t('adminDashboard.userManagementDescription'),
      icon: Users,
      color: 'bg-blue-500',
      action: () => { }
    },
    {
      title: t('adminDashboard.systemSettings'),
      description: t('adminDashboard.systemSettingsDescription'),
      icon: Settings,
      color: 'bg-green-500',
      action: () => { }
    },
    {
      title: t('adminDashboard.securityLogs'),
      description: t('adminDashboard.securityLogsDescription'),
      icon: Shield,
      color: 'bg-red-500',
      action: () => { }
    },
    {
      title: t('adminDashboard.dataExport'),
      description: t('adminDashboard.dataExportDescription'),
      icon: Download,
      color: 'bg-purple-500',
      action: () => { }
    },
    {
      title: t('adminDashboard.systemMonitor'),
      description: t('adminDashboard.systemMonitorDescription'),
      icon: Server,
      color: 'bg-orange-500',
      action: () => { }
    },
    {
      title: t('adminDashboard.backupRestore'),
      description: t('adminDashboard.backupRestoreDescription'),
      icon: Database,
      color: 'bg-indigo-500',
      action: () => { }
    }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'success':
        return 'text-success-600 bg-success-50';
      case 'warning':
        return 'text-warning-600 bg-warning-50';
      case 'error':
        return 'text-error-600 bg-error-50';
      default:
        return 'text-secondary-600 bg-secondary-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 spinner mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-secondary-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={`transition-all duration-300 ${isRTL ? 'lg:mr-64 lg:ml-0' : 'lg:ml-64 lg:mr-0'}`}>
        <TopNav onSidebarToggle={() => setIsSidebarOpen(true)} />

        <main className="p-6">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              {t('adminDashboard.title')}
            </h1>
            <p className="text-secondary-600">
              {t('adminDashboard.subtitle')}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="stat-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className={`flex items-center space-x-1 text-sm ${stat.changeType === 'increase' ? 'text-success-600' : 'text-error-600'
                      }`}>
                      {stat.changeType === 'increase' ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4" />
                      )}
                      <span>{stat.change}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-1">
                    {stat.value}
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    {stat.title}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* System Activity
            <div className="lg:col-span-2">
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('adminDashboard.systemActivity')}</h2>
                  <p className="text-secondary-600 text-sm">{t('adminDashboard.systemActivityDescription')}</p>
                </div>
                <div className="card-body">
                  <div className="space-y-4">
                    {systemActivity.map((activity) => (
                      <div key={activity.id} className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'} p-3 rounded-lg hover:bg-secondary-50 transition-colors`}>
                        <div className={`w-10 h-10 ${getSeverityColor(activity.severity)} rounded-full flex items-center justify-center`}>
                          <Activity className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-secondary-900">
                            {activity.action}
                          </p>
                          <p className="text-xs text-secondary-500">
                            by {activity.user}
                          </p>
                        </div>
                        <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-1'} text-xs text-secondary-500`}>
                          <Clock className="w-3 h-3" />
                          <span>{activity.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
 */}
            {/* Admin Actions */}
            <div>
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('adminDashboard.adminActions')}</h2>
                  <p className="text-secondary-600 text-sm">{t('adminDashboard.adminActionsDescription')}</p>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    {adminActions.map((action, index) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => action.action && action.action()}
                          className={`w-full flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} p-3 rounded-lg hover:bg-secondary-50 transition-colors ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-secondary-900">
                              {action.title}
                            </p>
                            <p className="text-xs text-secondary-500">
                              {action.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
